import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, ArrowLeft, Calendar, User, Share2, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareButtons = ({ post, siteUrl }) => {
  const postUrl = `${siteUrl}/newsletter/${post.slug || post.id}`;
  const title = encodeURIComponent(post.title);
  const text = encodeURIComponent(post.excerpt || post.title);

  return (
    <div className="flex items-center gap-3" data-testid="share-buttons">
      <span className="text-sm text-gray-500 flex items-center gap-1">
        <Share2 className="w-4 h-4" /> Share:
      </span>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-500 hover:text-blue-600 transition-colors text-sm font-medium"
        data-testid="share-facebook"
      >
        Facebook
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(postUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-500 hover:text-sky-500 transition-colors text-sm font-medium"
        data-testid="share-twitter"
      >
        X / Twitter
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-500 hover:text-blue-700 transition-colors text-sm font-medium"
        data-testid="share-linkedin"
      >
        LinkedIn
      </a>
      <a
        href={`mailto:?subject=${title}&body=${text}%0A%0ARead more: ${encodeURIComponent(postUrl)}`}
        className="text-gray-500 hover:text-brand-gold transition-colors text-sm font-medium"
        data-testid="share-email"
      >
        Email
      </a>
    </div>
  );
};

const SubscribeForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'newsletter_page' })
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-brand-dark p-8" data-testid="subscribe-form">
      <div className="flex items-center gap-3 mb-3">
        <Mail className="w-5 h-5 text-brand-gold" />
        <h3 className="text-lg font-semibold text-white">Stay Connected</h3>
      </div>
      <p className="text-gray-400 text-sm mb-4">
        Subscribe to receive updates on new posts, events, and community news. You are not alone.
      </p>
      {status === 'success' ? (
        <p className="text-brand-gold font-medium" data-testid="subscribe-success">
          You are subscribed. Thank you for joining our community.
        </p>
      ) : (
        <form onSubmit={handleSubscribe} className="flex gap-3">
          <Input
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            data-testid="subscribe-email-input"
          />
          <button
            type="submit"
            disabled={submitting}
            className="bg-brand-gold px-6 py-2 text-sm font-semibold text-brand-dark hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            data-testid="subscribe-submit-btn"
          >
            {submitting ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      )}
      {status === 'error' && (
        <p className="text-red-400 text-sm mt-2">Something went wrong. Please try again.</p>
      )}
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const estimateReadTime = (content) => {
  const words = content.split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

// Single post view
const PostDetail = ({ postId }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const siteUrl = window.location.origin;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/newsletter-posts/${postId}`);
        if (res.ok) {
          setPost(await res.json());
        }
      } catch (err) {
        console.error('Error fetching post:', err);
      }
      setLoading(false);
    };
    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Post not found.</p>
          <Link to="/newsletter" className="text-brand-gold hover:underline">
            Back to Newsletter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="post-detail">
      {/* Back nav */}
      <div className="mb-8">
        <Link
          to="/newsletter"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-gold transition-colors"
          data-testid="back-to-newsletter"
        >
          <ArrowLeft className="w-4 h-4" />
          All Posts
        </Link>
      </div>

      <article className="max-w-3xl">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold text-brand-dark mb-4" data-testid="post-title">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            {post.author_name && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" /> {post.author_name}
              </span>
            )}
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" /> {formatDate(post.published_at)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {estimateReadTime(post.content)}
            </span>
          </div>
        </header>

        <div
          className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
          data-testid="post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <div className="mt-10 pt-6 border-t border-gray-200">
          <ShareButtons post={post} siteUrl={siteUrl} />
        </div>
      </article>

      <div className="mt-12">
        <SubscribeForm />
      </div>
    </div>
  );
};

// Newsletter listing
const NewsletterPage = () => {
  const { postId } = useParams();
  usePageTitle(postId ? 'Newsletter Post' : 'Newsletter');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const siteUrl = window.location.origin;

  useEffect(() => {
    if (!postId) {
      const fetchPosts = async () => {
        try {
          const res = await fetch(`${API}/newsletter-posts?status=published`);
          if (res.ok) {
            setPosts(await res.json());
          }
        } catch (err) {
          console.error('Error fetching posts:', err);
        }
        setLoading(false);
      };
      fetchPosts();
    } else {
      setLoading(false);
    }
  }, [postId]);

  if (postId) {
    return (
      <div className="bg-white min-h-screen">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 py-16">
          <PostDetail postId={postId} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="newsletter-page">
      {/* Hero */}
      <div className="bg-brand-dark py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-semibold text-white mb-4" data-testid="newsletter-heading">
            Newsletter
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            Updates, stories, and resources from the ElevateMN community.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 lg:px-8 py-16">
        {/* Subscribe form at top */}
        <div className="mb-12">
          <SubscribeForm />
        </div>

        {/* Posts */}
        {loading ? (
          <p className="text-gray-500 text-center">Loading posts...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No posts yet.</p>
            <p className="text-gray-400 text-sm">
              Subscribe above to be the first to know when we publish.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="newsletter-posts-list">
            {posts.map((post) => (
              <article
                key={post.id}
                className="border-b border-gray-100 pb-8 last:border-0"
                data-testid={`newsletter-post-${post.id}`}
              >
                <Link
                  to={`/newsletter/${post.slug || post.id}`}
                  className="group"
                >
                  <h2 className="text-xl font-semibold text-brand-dark group-hover:text-brand-gold transition-colors mb-2">
                    {post.title}
                  </h2>
                </Link>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                  {post.author_name && (
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /> {post.author_name}
                    </span>
                  )}
                  {post.published_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> {formatDate(post.published_at)}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {estimateReadTime(post.content)}
                  </span>
                </div>
                {post.excerpt && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    {post.excerpt}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <Link
                    to={`/newsletter/${post.slug || post.id}`}
                    className="text-sm font-medium text-brand-gold hover:underline"
                  >
                    Read more
                  </Link>
                  <ShareButtons post={post} siteUrl={siteUrl} />
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterPage;
